import React from "react"
import PropTypes from "prop-types"
import { useOutletContext } from "react-router-dom"
import { Tooltip, Space, Button, Dropdown } from "antd"
import { EnterOutlined, WarningOutlined, CloseOutlined } from "@ant-design/icons"

import { JOINT, INDIVIDUAL } from "@api/services/investments/shapes/ProfileTypeEnum"

import InvestorColumn from "../InvestorColumn"
import { InvestmentShape } from "../shapes"
import computeInvestmentName from "../helpers/computeInvestmentName"

const LABEL_LINK = "Link Profile"


const TableInvestmentColumn = ({
  isIncluded,
  investment,
  sourceProjectId,
  targetInvestments,
  customTargets,
  setCustomTargets,
}) => {
  const { computeAccountFullname } = useOutletContext()

  const name = computeInvestmentName(computeAccountFullname, investment)

  const isSourceInvestment = investment.projectId === sourceProjectId
  const isTargetInvestment = !isSourceInvestment

  if (isTargetInvestment) {
    const isCustomTargetInvestment = !!customTargets
      .find(({ targetInvestmentId }) => investment.id === targetInvestmentId)

    const unlinkCustomInvestment = () =>
      setCustomTargets(prev =>
        [ ...prev.filter(({ targetInvestmentId }) => investment.id !== targetInvestmentId) ]
      )

    return (
      <Space>
        <EnterOutlined style={{ transform: 'scaleX(-1) translateY(-3px)' }} />

        <InvestorColumn
          name={<>{name}</>}
          isBold={isIncluded}
          investment={investment}
        />

        {
          isCustomTargetInvestment && (
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={unlinkCustomInvestment}
            />
          )
        }
      </Space>
    )
  }

  const { investorAccountId, profileType } = investment

  const listTargetInvestmentsForType = (targetProfileType) =>
    targetInvestments
      .filter(targetInvestment => targetInvestment.investorAccountId === investorAccountId)
      .filter(targetInvestment => targetInvestment.profileType === targetProfileType)

  const isIndividual = profileType === INDIVIDUAL
  const isJoint = profileType === JOINT
  const isPersonal = isIndividual || isJoint

  const getRelatedTargetInvestments = () => {
    if (isPersonal) {
      return [
        ...listTargetInvestmentsForType(INDIVIDUAL),
        ...listTargetInvestmentsForType(JOINT)
      ]
    }

    return listTargetInvestmentsForType(profileType)
  }

  const relatedTargetInvestments = getRelatedTargetInvestments()

  const hasRelatedInvestments =
    isIncluded &&
    relatedTargetInvestments.length > 0

  const getTooltipTitle = () => {
    if (!isPersonal) {
      return "The target project has investment profiles of the same type — ensure they are distinct."
    }

    if (isIndividual) {
      return "The target project has individual profiles — ensure they are distinct."
    }

    return "The target project has joint profiles — ensure they are distinct."
  }

  const linkTargetInvestment = (targetInvestmentId) => {
    const sourceInvestmentId = investment.id
    setCustomTargets(prev => [...prev, { sourceInvestmentId, targetInvestmentId }])
  }

  return (
    <Space>
      <InvestorColumn
        name={<>{name}</>}
        isBold={isIncluded}
        investment={investment}
      />

      {
        hasRelatedInvestments && (
          <Dropdown
            menu={{
              items: relatedTargetInvestments
                .map(({ id: key, name: label }) => ({
                  onClick: () => linkTargetInvestment(key),
                  key,
                  label,
                }))
            }}
            arrow={true}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
            >
              <Tooltip
                title={getTooltipTitle()}
                placement="top"
              >
                <WarningOutlined style={{ color: "red" }} />
              </Tooltip>

              {LABEL_LINK}
            </Button>
          </Dropdown>
        )
      }
    </Space>
  )
}

TableInvestmentColumn.propTypes = {
  isIncluded: PropTypes.bool.isRequired,
  investment: InvestmentShape.isRequired,
  sourceProjectId: PropTypes.string.isRequired,
  targetInvestments: PropTypes.arrayOf(InvestmentShape).isRequired,
  customTargets: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  setCustomTargets: PropTypes.func.isRequired,
}

export default TableInvestmentColumn
